/**
 * /admin/orgs — superadmin-only org directory + provisioning surface.
 *
 * Provisioning model: an org is a tenant with billing,
 * governance, and support footprint, qualitatively different from a
 * workspace (an intra-org action). Spinning up an org is therefore a
 * privileged admin operation, not a self-serve affordance on any
 * picker. The frontend Create-org button lives ONLY on this page; the
 * `OrgWorkspacePicker` deliberately omits it.
 *
 * Two surfaces on one page:
 *   - Directory table: every active org with first-owner email and
 *     workspace count. Filterable by name substring.
 *   - "New organization" modal: name + ownerEmail. On submit, calls
 *     `POST /api/admin/orgs` which resolves the email to a user and
 *     enrols them as owner. The calling superadmin is NOT added as a
 *     member — better-auth's createOrganization honors the explicit
 *     userId. Missing-email returns a 400 with a clear message
 *     ("they must sign up first") which we surface inline on the form.
 *
 * The frontend's `<RequireSuperadmin>` wrapper around `/admin/*` is UX
 * defense-in-depth; the actual data boundary is the server's
 * `requireSession + requireSuperadmin` chain on `/api/admin/orgs`.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { adminOrgsAPI, type AdminOrgRow } from '@/lib/api'
import { useQueryParamState } from '@/hooks/useQueryParamState'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'
import { CiSettingsModal } from './CiSettingsModal'

export function OrgsPage() {
  const tz = useUserTimezone()
  const [rows, setRows] = useState<AdminOrgRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Filter lives in the URL so navigate-away-and-return restores the
  // operator's active query (#1217). Strips on empty so a cleared
  // filter leaves no `?q=` residue.
  const [filter, setFilter] = useQueryParamState('q', '')
  const [createOpen, setCreateOpen] = useState(false)
  // Per-row CI settings modal (#554 PR 2). Holds the org being edited;
  // null = closed. Single modal mount rather than one per row to keep
  // the table flat.
  const [ciSettingsOrg, setCiSettingsOrg] = useState<AdminOrgRow | null>(null)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const r = await adminOrgsAPI.list()
      setRows(r)
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load orgs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const visible = useMemo(() => {
    if (!rows) return []
    const needle = filter.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(needle) ||
        r.slug.toLowerCase().includes(needle) ||
        (r.ownerEmail ?? '').toLowerCase().includes(needle),
    )
  }, [rows, filter])

  if (loading && !rows) {
    return <p className="text-sm text-muted-foreground">Loading orgs…</p>
  }
  if (loadError && !rows) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {loadError}
      </p>
    )
  }
  if (!rows) return null

  return (
    <div className="space-y-6 max-w-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Provision new tenants and review existing orgs. Creating an org is a
            privileged action — the designated owner must already have a user
            account.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>New organization</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Directory ({rows.length} active)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="orgs-filter">Filter</Label>
            <Input
              id="orgs-filter"
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Name, slug, or owner email"
              className="max-w-md"
              clearable
            />
            {filter && (
              <p className="text-xs text-muted-foreground">
                Showing {visible.length} of {rows.length} orgs
              </p>
            )}
          </div>

          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-right">Workspaces</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.slug}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.ownerEmail ?? (
                        <span className="text-muted-foreground italic">
                          (no owner)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{row.workspaceCount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(row.createdAt, tz)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCiSettingsOrg(row)}
                      >
                        CI settings
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                      {rows.length === 0
                        ? 'No orgs yet. Click "New organization" to provision one.'
                        : 'No orgs match the filter.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <CreateOrgDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setCreateOpen(false)
          void reload()
        }}
      />

      {ciSettingsOrg && (
        <CiSettingsModal
          open={ciSettingsOrg != null}
          onOpenChange={(o) => !o && setCiSettingsOrg(null)}
          org={ciSettingsOrg}
        />
      )}

    </div>
  )
}

// ── Create modal ───────────────────────────────────────────────────────

function CreateOrgDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset when the dialog opens so a re-open after a successful
  // create doesn't show stale draft state.
  useEffect(() => {
    if (open) {
      setName('')
      setOwnerEmail('')
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim() || !ownerEmail.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await adminOrgsAPI.create({ name: name.trim(), ownerEmail: ownerEmail.trim() })
      onCreated()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create org')
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = name.trim().length > 0 && ownerEmail.trim().length > 0 && !submitting

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New organization</DialogTitle>
            <DialogDescription>
              The designated owner must already have a user account. They will
              be enrolled as the first owner; you will not be added as a member.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="new-org-name">Org name</Label>
              <Input
                id="new-org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc"
                disabled={submitting}
                autoFocus
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-org-owner">Owner email</Label>
              <Input
                id="new-org-owner"
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="owner@example.com"
                disabled={submitting}
                required
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? 'Creating…' : 'Create organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default OrgsPage
